import { DialogTitle } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalWrapper,
} from "@songtradr-one/components-frontend";
import {
  CenterActions,
  Footer,
  Header,
  ProgressBar,
  ProgressFill,
  Props,
  RightButtons,
  StepsScrollArea,
  WizardContext,
  WizardOverlay,
  Wrapper,
  text,
  useWizardController,
} from "./Wizard.Parts";
import { WizardStep } from "./wizard-step/WizardStep";

export const Wizard = (props: Props) => {
  const {
    open,
    continueButtonText = text.continue,
    backButtonText = text.back,
    cancelButtonText = text.cancel,
    completeButtonText = text.complete,
    canComplete = true,
    loading = false,
  } = props;

  const {
    pageConfig,
    currentStep,
    childArray,
    isLastStep,
    progress,
    canContinue,
    canGoBack,
    contextValue,
    handleCancel,
    handleBack,
    handleContinue,
    handleOpenChange,
  } = useWizardController(props);

  if (!open) return null;

  return (
    <Modal open={open} onOpenChange={handleOpenChange} modal={false}>
      <WizardOverlay />
      <ModalWrapper
        $width="62.5rem"
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <VisuallyHidden>
          <DialogTitle>{pageConfig?.title}</DialogTitle>
        </VisuallyHidden>
        <Wrapper $hidden={!pageConfig}>
          <WizardContext.Provider value={contextValue}>
            {pageConfig && (
              <>
                <Header>
                  <Heading variant="section">{pageConfig.title}</Heading>
                  <Heading variant="h3-strong">{pageConfig.subtitle}</Heading>
                </Header>
                <Divider />
              </>
            )}
            <StepsScrollArea>
              {childArray.map((child, index) => {
                const isActive = index === currentStep;

                return (
                  <WizardStep key={index} isActive={isActive}>
                    {child}
                  </WizardStep>
                );
              })}
            </StepsScrollArea>
          </WizardContext.Provider>

          <ProgressBar>
            <ProgressFill $progress={progress} />
          </ProgressBar>

          {pageConfig && (
            <Footer>
              <Button
                $variant="subtle"
                $size="normal"
                disabled={loading ? "functional" : false}
                onClick={handleCancel}
              >
                {cancelButtonText}
              </Button>
              {pageConfig.actions && (
                <CenterActions>{pageConfig.actions}</CenterActions>
              )}
              <RightButtons>
                {canGoBack && (
                  <Button
                    $variant="subtle"
                    $size="normal"
                    disabled={loading ? "functional" : false}
                    onClick={handleBack}
                  >
                    {backButtonText}
                  </Button>
                )}
                {canComplete && (
                  <Button
                    $variant="primary"
                    $size="normal"
                    disabled={!canContinue}
                    onClick={handleContinue}
                    loading={loading}
                  >
                    {pageConfig.continueButtonText ??
                      (isLastStep ? completeButtonText : continueButtonText)}
                  </Button>
                )}
              </RightButtons>
            </Footer>
          )}
        </Wrapper>
      </ModalWrapper>
    </Modal>
  );
};
